<template>
  <TTabsToggle
    :header="isCustom ? customName : networkName"
    toggle-show
    :default-displayed="network.attributes.show"
    :no-move="!network.attributes.show"
    @show-block="$emit('show', network.attributes.name)"
    @hide-block="$emit('hide')"
  >
    <template #icon>
      <img
        v-if="!isCustom"
        class="h-[24px] w-[24px]"
        :src="`https://s3-eu-west-1.amazonaws.com/ecomail-assets/editor/social-icos/${selectedStyle}/${network.attributes.name
          .toLowerCase()
          .replace('-noshare', '')}.png`"
        alt="Facebook"
      />
      <img
        v-if="isCustom"
        class="h-[20px] w-[20px]"
        :src="customIconSrc"
        alt="Custom Social Media"
      />
    </template>

    <menu-content-container
      v-if="!isCustom"
      :padding-left-right="8"
      :border-bottom="false"
    >
      <t-auto-complete
        v-model="href"
        :data-testid="'network-link-' + networkName"
        :label="$t('forms.link')"
        style="margin-bottom: 12px"
        :has-icon="true"
        :flat-merge-tags="mergeTagsStore.flatMergeTags"
        :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
        :smart-merge-tags="mergeTagsStore.smartMergeTags"
        @on-blur="parseHref()"
      ></t-auto-complete>
      <div
        v-if="hrefLangDiff"
        class="mb-2 inline-block cursor-pointer rounded-md bg-gray-500 px-2.5 font-inter text-[11px] leading-6 text-gray-100 transition duration-300 hover:bg-gray-400 hover:text-gray-50"
        @click="updateHrefGlobalMutation()"
      >
        {{ $t("forms.href_lang_diff") }}
      </div>

      <t-auto-complete
        v-model="alt"
        :data-testid="'network-alt-' + networkName"
        label="Alt"
        style="margin-bottom: 8px"
        :has-icon="true"
        :flat-merge-tags="mergeTagsStore.flatMergeTags"
        :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
        :smart-merge-tags="mergeTagsStore.smartMergeTags"
        @on-blur="mutationEmptyString = ''"
      ></t-auto-complete>

      <t-check-box
        v-model="isShareUrl"
        :data-testid="'network-is-share-url-' + networkName"
        :disabled="!supportsShareUrl"
        :label="$t('forms.is_share_url')"
        style="margin-bottom: 8px"
      ></t-check-box>
    </menu-content-container>

    <menu-content-container
      v-else
      :padding-left-right="8"
      :border-bottom="false"
    >
      <content-row>
        <ImageButtons v-model="src" show-replace :index="index" />
      </content-row>
      <content-row :margin-top="12">
        <t-input
          v-model="customName"
          data-testid="custom-network-name"
          :label="$t('forms.custom_name')"
        ></t-input>
      </content-row>
      <content-row>
        <t-auto-complete
          v-model="href"
          data-testid="custom-network-link"
          :label="$t('forms.icon_link')"
          :has-icon="true"
          :flat-merge-tags="mergeTagsStore.flatMergeTags"
          :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
          :smart-merge-tags="mergeTagsStore.smartMergeTags"
        ></t-auto-complete>
      </content-row>
      <content-row>
        <t-auto-complete
          v-model="alt"
          data-testid="custom-network-alt"
          :label="`${$t('forms.image_alt')}`"
          :has-icon="true"
          :flat-merge-tags="mergeTagsStore.flatMergeTags"
          :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
          :smart-merge-tags="mergeTagsStore.smartMergeTags"
        ></t-auto-complete>
      </content-row>
      <content-row>
        <t-auto-complete
          v-model="src"
          data-testid="custom-network-image-source"
          :label="`${$t('forms.image_url')} / ${$t('forms.dynamic_image')}`"
          :has-icon="true"
          :flat-merge-tags="mergeTagsStore.flatMergeTags"
          :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
          :smart-merge-tags="mergeTagsStore.smartMergeTags"
        ></t-auto-complete>
      </content-row>
      <content-row :margin-bottom="8" :margin-top="12">
        <TButton @click="$emit('hide')">
          <template #icon>
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </template>
          {{ $t("forms.remove_icon") }}
        </TButton>
      </content-row>
    </menu-content-container>
  </TTabsToggle>
</template>

<script lang="ts" setup>
  import ImageButtons from "@/forms/components/ImageButtons/ImageButtons.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useMergeTagsStore } from "@/stores/mergeTags";

  import {
    TInput,
    TCheckBox,
    TMergetagsAutocomplete as TAutoComplete,
    TTabsToggle,
    TButton,
  } from "@topol/twig-ui";

  import { useI18n } from "vue-i18n";
  import { computed, ref } from "vue";
  import validHref from "@/utils/validHref";

  const props = defineProps({
    network: {
      type: Object,
      required: true,
    },
    selectedStyle: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isCustom: {
      type: Boolean,
      default: true,
    },
    supportsShareUrl: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["show", "hide", "remove"]);

  const mjmlStore = useMjmlStore();
  const mergeTagsStore = useMergeTagsStore();
  const { t } = useI18n();

  const mutationEmptyString = ref("");

  const customName = computed({
    get() {
      const name = props.network.attributes["custom-name"];

      if (name) {
        return name;
      }

      return t("forms.custom_icon");
    },
    set(val: string) {
      mjmlStore.updateBlockChildrenAttribute({
        childIndex: props.index,
        prop: "custom-name",
        value: val,
      });
    },
  });

  const href = computed({
    get() {
      if (mutationEmptyString.value === "href") {
        return "";
      }
      return mjmlStore.getBlockChildrenAttribute(props.index, "href");
    },
    set(val: string) {
      if ((!val || val.trim() === "") && mjmlStore.mjml.langs) {
        mutationEmptyString.value = "href";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockChildrenAttribute({
        childIndex: props.index,
        prop: "href",
        value: val.trim(),
      });
    },
  });

  const hrefLangDiff = computed(() => {
    return mjmlStore.isBlockChildrenAttributeLangDiff(
      props.index,
      "href"
    ) as string;
  });

  const src = computed({
    get() {
      return props.network.attributes.src;
    },
    set(val: string) {
      mjmlStore.updateBlockChildrenAttribute({
        childIndex: props.index,
        prop: "src",
        value: val.trim(),
      });
    },
  });

  const isShareUrl = computed({
    get() {
      return (
        !props.network.attributes["name"].endsWith("-noshare") &&
        props.supportsShareUrl
      );
    },

    set(val?: boolean) {
      if (val !== undefined && val !== null) {
        let newName = props.network.attributes["name"];

        newName = newName.toLowerCase();

        if (!isShareUrl.value) {
          newName = newName.replace("-noshare", "");
        } else {
          newName += "-noshare";
        }

        mjmlStore.updateBlockChildrenAttribute({
          childIndex: props.index,
          prop: "name",
          value: newName,
        });
      }
    },
  });

  const alt = computed({
    get() {
      if (mutationEmptyString.value === "alt") {
        return "";
      }
      return mjmlStore.getBlockChildrenAttribute(props.index, "alt") || "";
    },

    set(val: string) {
      if (!val?.trim() && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "alt";
      } else {
        mutationEmptyString.value = "";
      }
      if (val !== undefined && val !== null) {
        mjmlStore.updateBlockChildrenAttribute({
          childIndex: props.index,
          prop: "alt",
          value: val,
        });
      }
    },
  });

  const customIconSrc = computed(() => {
    if (src.value) {
      return src.value;
    } else {
      return `../assets/social-icons/unknown-custom.svg`;
    }
  });

  const networkName = computed(() => {
    const name = props.network.attributes.name;
    const strippedName = name.toLowerCase().replace("-noshare", "");

    return strippedName.charAt(0).toUpperCase() + strippedName.slice(1);
  });

  function parseHref() {
    mutationEmptyString.value = "";
    if (!validHref(href.value)) {
      href.value = "http://" + href.value;
    }
  }

  function updateHrefGlobalMutation() {
    mjmlStore.updateBlockChildrenAttributeForAllMutations({
      childIndex: props.index,
      prop: "href",
      value: href.value,
    });
  }
</script>
<style lang="scss"></style>
